import { type Reshape, type Similarity } from '@/domain/construction';
import { type Bounds } from '@/domain/geometry';
import { EPSILON, type Vec2, vec } from '@/domain/math';
import { type TransformBox } from '@/rendering';

/**
 * The box a user drags to move, turn and resize a selection.
 *
 * ## Why the frame belongs to the tool and not to the scene
 *
 * The obvious implementation recomputes the box from `selectionBounds` on every pointer move.
 * It is wrong twice over, and both failures are invisible until you try it.
 *
 * *During* a drag, the box is what the drag is measured against. Recomputing it means the
 * anchor moves as you pull away from it, so the pointer-to-scale mapping is a positive
 * feedback loop and the figure runs away from the cursor.
 *
 * *Between* drags it is worse. `selectionBounds` returns an axis-aligned box, so a figure
 * turned by 45° reports a box √2 larger than the one the user was just dragging. Let go and
 * the box jumps outward; turn it back and it shrinks again. The handles move under the hand,
 * and the same drag distance means a different amount every time.
 *
 * So a `TransformFrame` is *seeded* from the selection's bounds and thereafter carried by the
 * tool: each committed gesture turns or scales it, and nothing else touches it. It is not
 * geometry, it never enters the document, and it is not worth an undo step — the same
 * argument `ToolController.angleMovers` makes for the point an angle moves.
 *
 * ## Why this module is pure
 *
 * Every function here takes numbers and returns numbers. There is no scene, no controller and
 * no canvas, so the arithmetic that decides where a figure lands can be tested directly rather
 * than through a simulated drag. That matters because the failures worth guarding against are
 * arithmetic ones: a scale that goes negative, a frame with no extent, a division by a
 * distance that happens to be zero.
 *
 * ## Screen sizes are the caller's problem
 *
 * Handles are a fixed size in *pixels*, but everything here is in world units. Rather than
 * import a zoom, each entry point takes a `HandleGeometry` whose lengths the caller has
 * already divided by the zoom. The painter does the same conversion in the other direction,
 * and because `toScreen` is a similarity the two agree exactly at every zoom level.
 */

/**
 * An oriented box: a centre, two unit axes, and the full extent along each.
 *
 * The centre rather than a corner, because a rotation is about the centre and a corner would
 * have to be recomputed to express that. The axes rather than an angle, because the reshape
 * gesture needs to work in a frame that is not the world's and an angle would have to be
 * turned back into axes at every use.
 */
export interface TransformFrame {
  readonly center: Vec2;
  /** Unit. */
  readonly axisX: Vec2;
  /** Unit, and a quarter turn counter-clockwise from `axisX`. */
  readonly axisY: Vec2;
  /** Full extent along `axisX`, never negative. */
  readonly width: number;
  /** Full extent along `axisY`, never negative. */
  readonly height: number;
}

/**
 * Which grip is in hand.
 *
 * Named by compass point in the *frame's* own orientation, not the world's: `n` is the `+axisY`
 * edge whichever way the frame has been turned. A user dragging the top of a tilted box is
 * dragging its top, and calling that something else would only be true from the world's point
 * of view, which is not where the user is standing.
 */
export const Handle = {
  SouthWest: 'sw',
  South: 's',
  SouthEast: 'se',
  East: 'e',
  NorthEast: 'ne',
  North: 'n',
  NorthWest: 'nw',
  West: 'w',
  Rotate: 'rotate',
} as const;
export type Handle = (typeof Handle)[keyof typeof Handle];

/** The four corners, in the order `cornersOf` returns them. */
export const CORNER_HANDLES: readonly Handle[] = [
  Handle.SouthWest,
  Handle.SouthEast,
  Handle.NorthEast,
  Handle.NorthWest,
];

/** The four edge midpoints. */
export const EDGE_HANDLES: readonly Handle[] = [
  Handle.South,
  Handle.East,
  Handle.North,
  Handle.West,
];

/** What a similarity offers: corners turn and resize, edges would have to stretch. */
export const SIMILARITY_HANDLES: readonly Handle[] = [...CORNER_HANDLES, Handle.Rotate];

/** What a per-axis reshape offers: everything. */
export const RESHAPE_HANDLES: readonly Handle[] = [
  ...CORNER_HANDLES,
  ...EDGE_HANDLES,
  Handle.Rotate,
];

/** Handle sizes, in world units — the caller divides pixels by the zoom. */
export interface HandleGeometry {
  /** How near the pointer must come to a handle to take hold of it. */
  readonly radius: number;
  /** How far beyond the `n` edge the rotation grip sits. */
  readonly rotateOffset: number;
}

/**
 * How near the pointer must come to a grip, in screen pixels.
 *
 * A little larger than `OverlayStyle.handleSize`, which decides how large a grip is *drawn*.
 * They are separate numbers because they answer separate questions, but they have to stay
 * within sight of each other: a grip drawn large and picked small feels broken, and nothing
 * reports it.
 */
export const HANDLE_RADIUS_PX = 8;

/** How far beyond the top edge the rotation grip sits, in screen pixels. */
export const ROTATE_OFFSET_PX = 22;

/** The world-space handle sizes at a given zoom. */
export function geometryFor(zoom: number): HandleGeometry {
  const safe = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  return { radius: HANDLE_RADIUS_PX / safe, rotateOffset: ROTATE_OFFSET_PX / safe };
}

/**
 * The frame as the painter wants it: positions, not names.
 *
 * The conversion is here rather than in the painter because this module is the one that knows
 * where a grip belongs, and sending names instead would leave two copies of the same
 * vocabulary to be kept in agreement by hand.
 */
export function boxOf(
  frame: TransformFrame,
  geometry: HandleGeometry,
  handles: readonly Handle[],
  readout: { readonly at: Vec2; readonly text: string } | null = null
): TransformBox {
  const offered = new Set(handles);

  return {
    corners: cornersOf(frame),
    grips: [...CORNER_HANDLES, ...EDGE_HANDLES]
      .filter((handle) => offered.has(handle))
      .map((handle) => handleAt(frame, handle, geometry)),
    rotate: offered.has(Handle.Rotate)
      ? {
          at: handleAt(frame, Handle.Rotate, geometry),
          from: handleAt(frame, Handle.North, geometry),
        }
      : null,
    readout,
  };
}

export interface DragOptions {
  /** Resize about the frame's centre rather than about the opposite handle. */
  readonly aboutCenter?: boolean;
  /** Let the rotation land anywhere, instead of on a multiple of `ROTATION_STEP`. */
  readonly freeRotation?: boolean;
  /** Lock a reshape's two factors together, so the shape keeps its proportions. */
  readonly uniform?: boolean;
}

/**
 * The turn a drag settles on when nothing says otherwise: fifteen degrees.
 *
 * This is the whole answer to the objection recorded in `TransformPanel` — that a dragged
 * corner can only say *about* ninety degrees. A drag that snaps says exactly ninety, because
 * the number it produces is `6 * (π / 12)` and not whatever the pointer happened to be near.
 * Thirty and forty-five, the two amounts a geometry problem actually asks for, are both
 * multiples of it. Holding the primary modifier turns it off, which is the same escape the
 * rest of the editor uses for snapping.
 */
export const ROTATION_STEP = Math.PI / 12;

/**
 * The smallest a resize may leave a figure.
 *
 * Not zero, and not merely positive. A scale that reaches zero collapses every distance in the
 * selection and no later drag can recover them, because the ratios they would be restored from
 * are gone. A *negative* scale is worse than useless: `applySimilarity` scales the offset
 * *after* turning it, so a negative factor is a half turn about the anchor rather than the
 * mirror image a user dragging a corner past it would expect — and it writes negative numbers
 * into sources that store a length, where `bounds.ofCircle` computes `centre ± radius` with no
 * absolute value and hands the spatial index an inside-out box. The entity then vanishes from
 * the drawing without an error anywhere. Mirroring, if it is ever wanted, is a reshape with a
 * negative factor on one axis, not a similarity with a negative scale.
 */
export const MIN_SCALE = 1e-3;

/** A degenerate frame is one with no extent to measure a drag against. */
const MIN_EXTENT = 1e-6;

/**
 * The upright frame round a box.
 *
 * The seed, used when the selection changes. Everything after that comes from `frameAfter`.
 */
export function frameOf(box: Bounds): TransformFrame {
  return {
    center: vec.vec2((box.minX + box.maxX) / 2, (box.minY + box.maxY) / 2),
    axisX: vec.UNIT_X,
    axisY: vec.UNIT_Y,
    width: Math.abs(box.maxX - box.minX),
    height: Math.abs(box.maxY - box.minY),
  };
}

/**
 * A frame at an angle, for a shape that stores one.
 *
 * A turned ellipse's own box is not the upright box around it — the upright one is larger and
 * sits at a different angle to the shape — and only the shape's own box can be stretched without
 * shearing it. So where a source records its orientation, the frame is read from the source
 * rather than deduced from the drawing's bounds.
 */
export function orientedFrame(
  center: Vec2,
  rotation: number,
  width: number,
  height: number
): TransformFrame {
  return {
    center,
    axisX: vec.fromAngle(rotation),
    axisY: vec.fromAngle(rotation + Math.PI / 2),
    width: Math.abs(width),
    height: Math.abs(height),
  };
}

/**
 * The four corners, anticlockwise from the one at `-axisX, -axisY`.
 *
 * The order is part of the contract: the painter strokes them as a closed loop, and a loop
 * drawn in a different order is a bow tie.
 */
export function cornersOf(frame: TransformFrame): readonly [Vec2, Vec2, Vec2, Vec2] {
  return [
    handleAt(frame, Handle.SouthWest),
    handleAt(frame, Handle.SouthEast),
    handleAt(frame, Handle.NorthEast),
    handleAt(frame, Handle.NorthWest),
  ];
}

/** Where a handle sits, in world units. */
export function handleAt(frame: TransformFrame, handle: Handle, geometry?: HandleGeometry): Vec2 {
  const { center, axisX, axisY, width, height } = frame;
  const hx = width / 2;
  const hy = height / 2;

  const at = (x: number, y: number): Vec2 =>
    vec.add(center, vec.add(vec.scale(axisX, x), vec.scale(axisY, y)));

  switch (handle) {
    case Handle.SouthWest:
      return at(-hx, -hy);
    case Handle.South:
      return at(0, -hy);
    case Handle.SouthEast:
      return at(hx, -hy);
    case Handle.East:
      return at(hx, 0);
    case Handle.NorthEast:
      return at(hx, hy);
    case Handle.North:
      return at(0, hy);
    case Handle.NorthWest:
      return at(-hx, hy);
    case Handle.West:
      return at(-hx, 0);
    case Handle.Rotate:
      /*
       * Above the top edge rather than beyond a corner. A corner already has a grip on it, and
       * two targets a few pixels apart is a coin toss for anyone not using a mouse.
       */
      return at(0, hy + (geometry?.rotateOffset ?? 0));
  }
}

/**
 * Which handle the pointer has hold of, if any.
 *
 * Corners are tested before edges because a corner lies at the end of two edges: on a small
 * box every corner is within reach of two edge midpoints as well, and nearest-wins alone would
 * hand back whichever the loop happened to reach first. The rotation grip is tested before
 * both because it lies outside the box entirely and cannot be confused with either.
 */
export function hitHandle(
  frame: TransformFrame,
  world: Vec2,
  geometry: HandleGeometry,
  available: readonly Handle[] = SIMILARITY_HANDLES
): Handle | null {
  if (!isUsable(frame)) return null;

  const offered = new Set(available);
  const radiusSquared = geometry.radius * geometry.radius;

  const nearestOf = (candidates: readonly Handle[]): Handle | null => {
    let best: Handle | null = null;
    let bestDistance = radiusSquared;

    for (const handle of candidates) {
      if (!offered.has(handle)) continue;
      const distance = vec.distanceSquared(world, handleAt(frame, handle, geometry));
      if (distance <= bestDistance) {
        best = handle;
        bestDistance = distance;
      }
    }
    return best;
  };

  return nearestOf([Handle.Rotate]) ?? nearestOf(CORNER_HANDLES) ?? nearestOf(EDGE_HANDLES);
}

/**
 * Whether a point lies inside the frame.
 *
 * Measured along the frame's own axes rather than against a bounding box, so a turned frame
 * answers about itself and not about the upright rectangle that contains it.
 */
export function containsPoint(frame: TransformFrame, world: Vec2): boolean {
  const offset = vec.subtract(world, frame.center);
  return (
    Math.abs(vec.dot(offset, frame.axisX)) <= frame.width / 2 &&
    Math.abs(vec.dot(offset, frame.axisY)) <= frame.height / 2
  );
}

/**
 * The cursor for a grip, turned with the frame.
 *
 * A fixed cursor per compass point would be a lie the moment the frame is turned: the grip a
 * frame calls its east one points north-east once the figure has been rotated by 45°, and an
 * arrow saying otherwise tells the hand to pull the wrong way. So the direction is measured from
 * the frame and bucketed into the four CSS resize cursors, which is all the resolution a cursor
 * has anyway.
 *
 * The y axis is flipped on the way, because the four names are screen directions and the frame
 * is in world space, where y points up.
 */
export function cursorFor(frame: TransformFrame, handle: Handle): string {
  // No standard rotation cursor exists, so the grab hand carries it — which is what it means
  // everywhere else in this editor.
  if (handle === Handle.Rotate) return 'grab';

  const outward = vec.subtract(handleAt(frame, handle), frame.center);
  if (vec.isZero(outward, MIN_EXTENT)) return 'default';

  // Screen space: y down. A half turn names the same pair of cursors, so this folds to [0, 180).
  const degrees = ((vec.angle(vec.vec2(outward.x, -outward.y)) * 180) / Math.PI + 360) % 180;
  const bucket = Math.round(degrees / 45) % 4;

  return RESIZE_CURSORS[bucket] ?? 'default';
}

/** Indexed by eighths of a turn from screen-east: →, ↗, ↑, ↘. */
const RESIZE_CURSORS: readonly string[] = ['ew-resize', 'nesw-resize', 'ns-resize', 'nwse-resize'];

/** The handle diagonally, or directly, across the frame from this one. */
export function oppositeOf(handle: Handle): Handle {
  switch (handle) {
    case Handle.SouthWest:
      return Handle.NorthEast;
    case Handle.South:
      return Handle.North;
    case Handle.SouthEast:
      return Handle.NorthWest;
    case Handle.East:
      return Handle.West;
    case Handle.NorthEast:
      return Handle.SouthWest;
    case Handle.North:
      return Handle.South;
    case Handle.NorthWest:
      return Handle.SouthEast;
    case Handle.West:
      return Handle.East;
    case Handle.Rotate:
      return Handle.Rotate;
  }
}

/**
 * The point a resize holds still.
 *
 * The opposite corner by default, which is what makes dragging feel like pulling the shape
 * rather than moving it: the far side stays where it was put. `aboutCentre` pins the middle
 * instead, so the figure grows evenly in both directions.
 */
export function anchorFor(frame: TransformFrame, handle: Handle, aboutCenter = false): Vec2 {
  if (aboutCenter || handle === Handle.Rotate) return frame.center;
  return handleAt(frame, oppositeOf(handle));
}

/**
 * The transform a drag from `grabbed` to `pointer` means, or `null` if it means nothing.
 *
 * `null` is returned rather than an identity so that a caller can tell "this gesture produced
 * no change" from "this gesture cannot be expressed" — the first should emit nothing, and the
 * second should never have been offered. It happens when the frame has no extent to measure
 * against, when the anchor and the grip coincide so there is no ratio to take, and whenever
 * the arithmetic would hand back a value that is not finite. That last check is the one that
 * matters most: a `NaN` coordinate is a perfectly valid edit, it spreads through every
 * construction downstream on the next evaluation, and it survives into the saved file.
 *
 * Edge handles yield `null` here. Pulling one edge is a per-axis scale, which is not a
 * similarity at all — `reshapeFromDrag` is where that lives.
 */
export function similarityFromDrag(
  frame: TransformFrame,
  handle: Handle,
  grabbed: Vec2,
  pointer: Vec2,
  options: DragOptions = {}
): Similarity | null {
  if (!isUsable(frame)) return null;

  if (handle === Handle.Rotate) {
    const center = frame.center;
    const from = vec.subtract(grabbed, center);
    const to = vec.subtract(pointer, center);
    if (vec.isZero(from, MIN_EXTENT) || vec.isZero(to, MIN_EXTENT)) return null;

    const turned = vec.angle(to) - vec.angle(from);
    const rotation = options.freeRotation === true ? turned : snapTurn(frame, turned);

    return finiteOrNull({ center, rotation, scale: 1, translate: vec.ZERO });
  }

  if (!CORNER_HANDLES.includes(handle)) return null;

  const anchor = anchorFor(frame, handle, options.aboutCenter === true);
  const reach = vec.distance(grabbed, anchor);
  if (reach < MIN_EXTENT) return null;

  const scale = Math.max(vec.distance(pointer, anchor) / reach, MIN_SCALE);

  return finiteOrNull({ center: anchor, rotation: 0, scale, translate: vec.ZERO });
}

/**
 * The per-axis stretch a drag from `grabbed` to `pointer` means, or `null` if it means nothing.
 *
 * The same contract as `similarityFromDrag`, and the same guards: a frame with no extent, an
 * anchor the grip already sits on, and any factor that comes out non-finite all yield `null`
 * rather than an edit that writes `NaN` into the document.
 *
 * ## Which factors a handle is allowed to change
 *
 * An **edge** grip moves one axis and leaves the other exactly alone — that is what makes it
 * worth having, and it is the whole reason this exists next to the similarity. A **corner** grip
 * moves both, independently, which is the ordinary free-transform behaviour. Holding shift locks
 * the two together, which is the habit every drawing program has taught, and produces a map that
 * is a similarity again.
 *
 * ## Why the factors are measured along the frame's axes
 *
 * Because the frame may be the shape's own rather than the world's — a turned ellipse can only
 * be stretched along its own axes without shearing. Projecting the drag onto the frame means
 * this arithmetic is identical in both cases, and the difference lives entirely in which frame
 * the caller supplies.
 */
export function reshapeFromDrag(
  frame: TransformFrame,
  handle: Handle,
  grabbed: Vec2,
  pointer: Vec2,
  options: DragOptions = {}
): Reshape | null {
  if (!isUsable(frame) || handle === Handle.Rotate) return null;

  const anchor = anchorFor(frame, handle, options.aboutCenter === true);
  const from = vec.subtract(grabbed, anchor);
  const to = vec.subtract(pointer, anchor);

  const factor = (axis: Vec2): number | null => {
    const reach = vec.dot(from, axis);
    if (Math.abs(reach) < MIN_EXTENT) return null;
    return vec.dot(to, axis) / reach;
  };

  const free = { x: factor(frame.axisX), y: factor(frame.axisY) };

  /*
   * An edge grip has no reach along the axis it does not move, so `factor` refuses there — and
   * refusing is right for a corner and wrong for an edge, where "no reach" means "leave it
   * alone". `1` is that instruction.
   */
  const spans = { x: MOVES_X.has(handle), y: MOVES_Y.has(handle) };
  let scaleX = spans.x ? free.x : 1;
  let scaleY = spans.y ? free.y : 1;
  if (scaleX === null || scaleY === null) return null;

  if (options.uniform === true) {
    /*
     * Locked together. The larger of the two wins rather than an average, so a drag that pulls
     * mostly sideways still grows the shape — an average would make the shape shrink whenever
     * one axis barely moved, which reads as the gesture fighting the hand.
     */
    const both = Math.abs(scaleX) >= Math.abs(scaleY) ? scaleX : scaleY;
    scaleX = both;
    scaleY = both;
  }

  const clamped = {
    x: clampFactor(scaleX, spans.x),
    y: clampFactor(scaleY, spans.y),
  };

  if (!Number.isFinite(clamped.x) || !Number.isFinite(clamped.y) || !vec.isFinite(anchor)) {
    return null;
  }

  return {
    origin: anchor,
    axisX: frame.axisX,
    axisY: frame.axisY,
    scaleX: clamped.x,
    scaleY: clamped.y,
  };
}

/** Which handles move the frame's `axisX` extent, and which move its `axisY` extent. */
const MOVES_X = new Set<Handle>([
  Handle.East,
  Handle.West,
  Handle.NorthEast,
  Handle.NorthWest,
  Handle.SouthEast,
  Handle.SouthWest,
]);
const MOVES_Y = new Set<Handle>([
  Handle.North,
  Handle.South,
  Handle.NorthEast,
  Handle.NorthWest,
  Handle.SouthEast,
  Handle.SouthWest,
]);

/**
 * A factor kept positive and away from zero.
 *
 * The same argument as `MIN_SCALE`, for the same reason: a negative factor writes a negative
 * radius or a negative width, and a source whose bounds are computed without an absolute value
 * hands the spatial index an inside-out box and disappears. An axis the handle does not move
 * keeps its factor of exactly one rather than being clamped, so that "leave it alone" survives.
 */
function clampFactor(factor: number, moved: boolean): number {
  if (!moved) return 1;
  return Math.max(Math.abs(factor), MIN_SCALE);
}

/**
 * The frame after a transform has been applied to it.
 *
 * How the tool carries the box forward across gestures. The axes are turned rather than
 * recomputed from the result, which is the whole point: the box a user sees after turning a
 * square by forty-five degrees is the square's own box, not the larger upright one that
 * contains it.
 */
export function frameAfter(frame: TransformFrame, transform: Similarity): TransformFrame {
  const { rotation, scale } = transform;

  return {
    center: applyTo(transform, frame.center),
    axisX: vec.normalize(vec.rotate(frame.axisX, rotation)),
    axisY: vec.normalize(vec.rotate(frame.axisY, rotation)),
    width: frame.width * scale,
    height: frame.height * scale,
  };
}

/** The turn of a frame away from upright, in radians. */
export function headingOf(frame: TransformFrame): number {
  return vec.angle(frame.axisX);
}

/** Whether a frame has enough extent for a drag to be measured against it. */
export function isUsable(frame: TransformFrame): boolean {
  return (
    frame.width >= MIN_EXTENT &&
    frame.height >= MIN_EXTENT &&
    vec.isFinite(frame.center) &&
    Number.isFinite(frame.width) &&
    Number.isFinite(frame.height)
  );
}

/*
 * Kept local rather than imported from `domain/construction` so that this module does not have
 * to agree with `applySimilarity` about anything but the answer — and the tests compare them.
 */
function applyTo(transform: Similarity, point: Vec2): Vec2 {
  const offset = vec.subtract(point, transform.center);
  const turned = vec.scale(vec.rotate(offset, transform.rotation), transform.scale);
  return vec.add(vec.add(transform.center, turned), transform.translate);
}

/**
 * The turn that leaves the frame's heading on a multiple of `ROTATION_STEP`.
 *
 * Snapping the *result* and not the *amount*. Snapping the amount would round a drag of 43°
 * to 45° and leave a frame already sitting at 7° pointing at 52°, which is no rounder than
 * where it started. What the user wants is the figure square to the page, and that is a
 * statement about where it ends up.
 */
function snapTurn(frame: TransformFrame, turned: number): number {
  const heading = headingOf(frame);
  const target = Math.round((heading + turned) / ROTATION_STEP) * ROTATION_STEP;
  return target - heading;
}

function finiteOrNull(transform: Similarity): Similarity | null {
  const finite =
    vec.isFinite(transform.center) &&
    vec.isFinite(transform.translate) &&
    Number.isFinite(transform.rotation) &&
    Number.isFinite(transform.scale) &&
    Math.abs(transform.scale) > EPSILON;

  return finite ? transform : null;
}
